import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import api from '../services/api';
import { IConfigDashboard } from '../constants/dashboards';
import { Dashboard, lerDashboard } from '../types/Dashboard';
import { Filtros } from './useFiltrosDashboard';

interface EstadoDashboard {
  /** null = a API devolveu 204 (sem dados no período ou sem datas) */
  dados: Dashboard | null | undefined;
  carregando: boolean;
  erro?: string;
}

function mensagemErro(error: any) {
  if (error?.code === 'ECONNABORTED') return 'O servidor demorou demais para responder.';
  const data = error?.response?.data;
  return data?.MENSAGEM || data?.ERRO || error?.message || 'Erro ao carregar o dashboard.';
}

// POST /dashboard?rel= com os filtros. Cada seção da tela carrega sozinha, sem o overlay global:
// a API atende um dashboard por vez e o FluCai pode levar até 2 minutos.
export default function useDashboard(dash: IConfigDashboard, filtros: Filtros) {
  const [estado, setEstado] = useState<EstadoDashboard>({ dados: undefined, carregando: true });
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    const controle = new AbortController();

    async function buscar() {
      setEstado(atual => ({ dados: atual.dados, carregando: true }));
      try {
        // chaves "_<cam>" guardam só a descrição exibida; não vão para a API
        const corpo = Object.fromEntries(Object.entries(filtros).filter(([chave]) => !chave.startsWith('_')));
        const response = await api.post('/dashboard', corpo, {
          params: { rel: dash.rel },
          timeout: dash.timeout ?? 60000,
          signal: controle.signal
        });

        if (response.status === 204 || !response.data) {
          setEstado({ dados: null, carregando: false });
          return;
        }

        const dashboard = lerDashboard(response.data);
        if (dash.widgets) {
          dashboard.widgets = dashboard.widgets.filter(widget => dash.widgets!.includes(widget.id));
        }
        setEstado({ dados: dashboard, carregando: false });
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error(error);
        setEstado({ dados: undefined, carregando: false, erro: mensagemErro(error) });
      }
    }

    buscar();

    return () => controle.abort();
  }, [dash, filtros, tentativa]);

  const recarregar = useCallback(() => setTentativa(t => t + 1), []);

  return { ...estado, recarregar };
}
